import { AlertCircle, RefreshCw } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "~/lib/utils";

interface DataLoadErrorProps extends Omit<ComponentProps<"div">, "title"> {
	title: ReactNode;
	description?: ReactNode;
	onRetry?: () => void;
	retrying?: boolean;
	retryLabel?: string;
}

/**
 * A quiet, reusable failure state for data-backed sections.
 * Critical domain warnings and form validation errors use their own patterns.
 */
export function DataLoadError({
	title,
	description,
	onRetry,
	retrying = false,
	retryLabel = "Zkusit znovu",
	className,
	...props
}: DataLoadErrorProps) {
	return (
		<div
			className={cn(
				"card-tonal flex flex-col gap-3 rounded-xl p-4 sm:flex-row sm:items-center",
				className,
			)}
			role="alert"
			aria-busy={retrying}
			{...props}
		>
			<div className="flex min-w-0 flex-1 items-start gap-3">
				<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-error/10">
					<AlertCircle className="h-4 w-4 text-error" aria-hidden="true" />
				</div>
				<div className="min-w-0 pt-0.5">
					<p className="text-sm font-semibold text-foreground">{title}</p>
					{description && (
						<p className="mt-0.5 text-xs leading-5 text-muted-foreground">{description}</p>
					)}
				</div>
			</div>
			{onRetry && (
				<button
					type="button"
					className="btn btn-sm btn-secondary-soft shrink-0 self-start sm:self-auto"
					onClick={onRetry}
					disabled={retrying}
				>
					<RefreshCw className={cn("h-4 w-4", retrying && "animate-spin")} aria-hidden="true" />
					{retrying ? "Načítám…" : retryLabel}
				</button>
			)}
		</div>
	);
}
